// IS THIS CUSTOMER ALREADY ON THE DATABASE?
//
// ⚠️ A WARNING, NEVER A REFUSAL. Owner's ruling, and the right one: two real
// companies can share a name, a group can have two entities that read alike,
// and a salesperson in front of a customer must not be stopped by a guess. So
// this returns a MATCH or nothing, the screen offers "Use existing" or "Create
// anyway", and there is no unique constraint anywhere behind it.
//
// ⚠️ IT WRITES NOTHING AND CHANGES NO EXISTING PARTNER. Finding a duplicate
// never merges, renames, archives or edits the record it found. The only two
// outcomes are "select the one that exists" and "create the new one anyway",
// and both are the person's choice.
//
// WHY IT EXISTS: `Marlowe` was created twice on live (partners 135 and 148)
// and nothing said a word at the time. 148 was archived by hand afterwards.
// See HANDOFF.md's Marlowe entry.
//
// ⚠️ THE NAME RULE IS `sameCompany` FROM poWarnings.ts, REUSED RATHER THAN
// RE-DERIVED. That function already decides whether two printed company names
// denote one company -- case, punctuation, Pvt/Private, Ltd/Limited, a site
// suffix after a comma, and WHOLE-ENTITY equality with no substring test. One
// implementation means the PO screen and the customer picker cannot drift into
// disagreeing about whether two names are the same company, which they would
// the first time either was edited alone.

import { sameCompany } from "@/services/poWarnings";
import type { CustomerPartner } from "@/types";

export type DuplicateReason = "name" | "gstin";

export interface DuplicateMatch {
  existing: CustomerPartner;
  reason: DuplicateReason;
}

/** GSTIN, comparable: upper-cased with punctuation and spaces removed.
 *
 *  ⚠️ NORMALISED BUT NOT VALIDATED. A GSTIN that does not look like one is
 *  still saved (see `gstinLooksWrong`, which warns and does not block), so a
 *  malformed one must still be comparable -- two records carrying the same
 *  wrong GSTIN are still the same company's paperwork. */
function gstinKey(value: string | null | undefined): string {
  if (typeof value !== "string") return "";
  return value.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
}

/** The term to search candidates on.
 *
 *  ⚠️ THE FIRST SIGNIFICANT WORD, NOT THE WHOLE NAME, and this is what makes
 *  the Pvt/Private case work at all. The customer search is a server-side
 *  `name ilike <term>`, so searching the full typed string "Larkspur India
 *  Private Limited" would never surface the stored "Larkspur India Pvt Ltd" --
 *  the very duplicate this exists to catch. Searching "Larkspur" surfaces both
 *  and `sameCompany` then decides.
 *
 *  A legal form on its own is skipped, so "Pvt Ltd Foo" searches "Foo" rather
 *  than returning every company in India. Returns "" when there is nothing
 *  worth searching on, which the caller treats as "no candidates". */
export function customerSearchTerm(name: string | null | undefined): string {
  const raw = (typeof name === "string" ? name : "").trim();
  if (!raw) return "";
  // Stop at a comma: a site suffix is not part of the entity's name.
  const entity = raw.split(",")[0];
  const skip = new Set(["pvt", "pvt.", "private", "ltd", "ltd.", "limited",
                        "the", "m/s", "m/s."]);
  for (const token of entity.split(/\s+/)) {
    const clean = token.replace(/[^A-Za-z0-9&]/g, "");
    if (!clean) continue;
    if (skip.has(token.toLowerCase()) || skip.has(clean.toLowerCase())) continue;
    return clean;
  }
  return "";
}

/** The existing customer this draft would duplicate, or null.
 *
 *  ⚠️ `candidates` MUST ALREADY BE ACTIVE-ONLY. The caller passes the customer
 *  search's results, and that endpoint's domain excludes archived partners via
 *  Odoo's own record rule -- so an archived duplicate is never offered, which
 *  is the owner's ruling. An archived record is one somebody deliberately took
 *  out of circulation; proposing it as "use this one instead" would undo that
 *  decision on their behalf.
 *
 *  ⚠️ GSTIN BEATS NAME, because it is the stronger claim. A GSTIN is a
 *  registration number: two records carrying one are the same registered
 *  entity whatever they are called, whereas two similar names may be two real
 *  companies. When both match, the GSTIN is reported as the reason.
 *
 *  ⚠️ FIRST MATCH WINS AND NOTHING IS RANKED. With more than one candidate
 *  this offers the first, because "which of these two duplicates did you
 *  mean" is a question this function cannot answer and must not pretend to.
 *  The person can always create anyway. */
export function findDuplicateCustomer(
  draftName: string | null | undefined,
  draftGstin: string | null | undefined,
  candidates: CustomerPartner[] | null | undefined,
): DuplicateMatch | null {
  const list = (candidates ?? []).filter(Boolean);
  if (!list.length) return null;

  const key = gstinKey(draftGstin);
  if (key) {
    for (const existing of list) {
      if (gstinKey(existing.vat) === key) return { existing, reason: "gstin" };
    }
  }

  const name = (draftName ?? "").trim();
  if (name) {
    for (const existing of list) {
      // ⚠️ sameCompany returns TRUE when either side is empty -- an unknown is
      // not a disagreement, which is right where it is used for a warning
      // about two printed names. Here an empty stored name must NOT count as
      // a duplicate of everything, so it is excluded first.
      if (!(existing.name ?? "").trim()) continue;
      if (sameCompany(name, existing.name)) return { existing, reason: "name" };
    }
  }

  return null;
}

/** The one sentence the screen shows. Said here so there is one wording. */
export function duplicateMessage(match: DuplicateMatch): string {
  return (
    `A customer called ${match.existing.name} already exists. `
    + `Use that one, or create a new one anyway?`
  );
}
